import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmtTRY, fmtDate, isKurumsal } from "@/lib/api";
import { FORM_SECTIONS, RISK_SORULARI, BELGE_TIPLERI } from "@/lib/formConfig";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Trash2, Upload, FileText, Plus, Download, Phone, Mail, Building2, User, Receipt, TrendingDown } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cust, setCust] = useState(null);
  const [txns, setTxns] = useState([]);
  const [docs, setDocs] = useState([]);
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnForm, setTxnForm] = useState({ tarih: todayISO(), aciklama: "", tur: "alacak", tutar: "", kategori: "tahsilat" });
  const [belgeTipi, setBelgeTipi] = useState("Vergi Levhası");
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get(`/customers/${id}`).then((r) => setCust(r.data));
    api.get(`/customers/${id}/transactions`).then((r) => setTxns(r.data));
    api.get(`/customers/${id}/documents`).then((r) => setDocs(r.data));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!cust) return <div className="p-8 text-zinc-500">Yükleniyor...</div>;
  const k = cust.kart || {};

  const addTxn = async () => {
    if (!txnForm.tutar || Number(txnForm.tutar) <= 0) { toast.error("Geçerli tutar girin."); return; }
    if (!txnForm.aciklama) { toast.error("Açıklama girin."); return; }
    await api.post(`/customers/${id}/transactions`, { ...txnForm, tutar: Number(txnForm.tutar) });
    toast.success("İşlem eklendi.");
    setTxnOpen(false);
    setTxnForm({ tarih: todayISO(), aciklama: "", tur: "alacak", tutar: "", kategori: "tahsilat" });
    load();
  };

  const tahakkukEt = async () => {
    const ucret = Number(k.aylik_hizmet_ucreti);
    if (!ucret) { toast.error("Önce kartta aylık hizmet ücreti tanımlayın."); return; }
    const ay = new Date().toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
    await api.post(`/customers/${id}/transactions`, { tarih: todayISO(), aciklama: `Aylık hizmet ücreti (${ay})`, tur: "borc", tutar: ucret, kategori: "hizmet_ucreti" });
    toast.success("Aylık ücret tahakkuk ettirildi.");
    load();
  };

  const delTxn = async (tid) => { await api.delete(`/transactions/${tid}`); toast.success("İşlem silindi."); load(); };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("belge_tipi", belgeTipi);
    try {
      await api.post(`/customers/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Belge yüklendi.");
      load();
    } catch { toast.error("Yükleme başarısız."); }
    e.target.value = "";
  };

  const delDoc = async (did) => { await api.delete(`/documents/${did}`); toast.success("Belge silindi."); load(); };

  const delCustomer = async () => { await api.delete(`/customers/${id}`); toast.success("Müşteri silindi."); navigate("/musteriler"); };

  const renderVal = (f, val) => {
    if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) return null;
    let display = val;
    if (f.type === "bool") display = val ? "Evet" : null;
    else if (f.type === "multi") display = val.join(", ");
    else if (f.type === "date") display = fmtDate(val);
    else if (f.key === "aylik_hizmet_ucreti") display = fmtTRY(val);
    if (!display) return null;
    return (
      <div key={f.key} className="py-2 border-b border-zinc-900 grid grid-cols-2 gap-2">
        <span className="text-xs text-zinc-500">{f.label}</span>
        <span className="text-sm text-zinc-200 break-words">{display}</span>
      </div>
    );
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <button onClick={() => navigate("/musteriler")} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4" data-testid="detail-back-btn">
        <ArrowLeft className="w-4 h-4" /> Müşteriler
      </button>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            {isKurumsal(k.tur) ? <Building2 className="w-6 h-6 text-zinc-400" /> : <User className="w-6 h-6 text-zinc-400" />}
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="detail-unvan">{k.unvan || "İsimsiz"}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <Badge variant="outline" className="rounded-none text-[10px] uppercase tracking-wider border-zinc-700 text-zinc-300">{k.tur || "-"}</Badge>
              {k.musteri_kodu && <span className="text-xs text-zinc-500 font-mono">{k.musteri_kodu}</span>}
              {k.risk_seviyesi && <Badge className={`rounded-none text-[10px] uppercase tracking-wider ${k.risk_seviyesi === "Yüksek" ? "bg-red-500/15 text-red-400 border-red-500/30" : k.risk_seviyesi === "Orta" ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" : "bg-green-500/15 text-green-400 border-green-500/30"}`}>Risk: {k.risk_seviyesi}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="edit-customer-btn" onClick={() => navigate(`/musteriler/${id}/duzenle`)} className="rounded-none border-zinc-700 hover:bg-zinc-900">
            <Pencil className="w-4 h-4 mr-1.5" /> Düzenle
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="delete-customer-btn" className="rounded-none border-red-900 text-red-400 hover:bg-red-950 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none bg-zinc-950 border-zinc-800">
              <AlertDialogHeader>
                <AlertDialogTitle>Müşteri silinsin mi?</AlertDialogTitle>
                <AlertDialogDescription>Bu işlem müşteriyi ve tüm cari hareketlerini kalıcı olarak siler.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none border-zinc-700">Vazgeç</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete-btn" onClick={delCustomer} className="rounded-none bg-red-600 hover:bg-red-500">Sil</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Left profile panel */}
        <div className="space-y-4">
          <div className="bg-zinc-950 border border-zinc-800 p-5">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Cari Bakiye</div>
            <div className={`text-3xl font-mono font-semibold ${cust.bakiye > 0 ? "text-yellow-400" : cust.bakiye < 0 ? "text-green-400" : ""}`} data-testid="detail-bakiye">{fmtTRY(cust.bakiye)}</div>
            <div className="text-xs text-zinc-500 mt-1">{cust.bakiye > 0 ? "Tahsil edilecek" : cust.bakiye < 0 ? "Fazla ödeme" : "Bakiye sıfır"}</div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 p-5 space-y-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">İletişim</div>
            {k.cep_telefonu && <div className="flex items-center gap-2.5 text-sm"><Phone className="w-4 h-4 text-zinc-500" /> <span className="font-mono">{k.cep_telefonu}</span></div>}
            {k.email && <div className="flex items-center gap-2.5 text-sm"><Mail className="w-4 h-4 text-zinc-500" /> <span className="break-all">{k.email}</span></div>}
            {k.firma_yetkilisi && <div className="text-sm"><span className="text-zinc-500">Yetkili: </span>{k.firma_yetkilisi}</div>}
            {k.vergi_dairesi && <div className="text-sm"><span className="text-zinc-500">V.D.: </span>{k.vergi_dairesi}</div>}
            {k.tc_vergi_no && <div className="text-sm"><span className="text-zinc-500">VKN/TCKN: </span><span className="font-mono">{k.tc_vergi_no}</span></div>}
            {k.aylik_hizmet_ucreti != null && k.aylik_hizmet_ucreti !== "" && <div className="text-sm"><span className="text-zinc-500">Aylık Ücret: </span><span className="font-mono">{fmtTRY(k.aylik_hizmet_ucreti)}</span></div>}
            {k.tahsilat_odeme_gunu && <div className="text-sm"><span className="text-zinc-500">Ödeme Günü: </span>Ayın {k.tahsilat_odeme_gunu}. günü</div>}
          </div>
        </div>

        {/* Right tabs */}
        <div className="bg-zinc-950 border border-zinc-800">
          <Tabs defaultValue="cari">
            <TabsList className="w-full justify-start rounded-none bg-transparent border-b border-zinc-800 p-0 h-auto">
              <TabsTrigger value="cari" data-testid="tab-cari" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-white data-[state=active]:shadow-none px-5 py-3">Cari Hesap</TabsTrigger>
              <TabsTrigger value="belgeler" data-testid="tab-belgeler" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-white data-[state=active]:shadow-none px-5 py-3">Belgeler</TabsTrigger>
              <TabsTrigger value="bilgiler" data-testid="tab-bilgiler" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-white data-[state=active]:shadow-none px-5 py-3">Kart Bilgileri</TabsTrigger>
            </TabsList>

            {/* Cari Hesap */}
            <TabsContent value="cari" className="p-5 mt-0">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Button data-testid="tahakkuk-btn" onClick={tahakkukEt} variant="outline" className="rounded-none border-zinc-700 hover:bg-zinc-900"><Receipt className="w-4 h-4 mr-1.5" /> Aylık Ücret Tahakkuk</Button>
                <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-txn-btn" className="rounded-none bg-white text-black hover:bg-zinc-200 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> İşlem Ekle</Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none bg-zinc-950 border-zinc-800">
                    <DialogHeader><DialogTitle>Cari İşlem Ekle</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-zinc-400">İşlem Türü</Label>
                          <Select value={txnForm.tur} onValueChange={(v) => setTxnForm((p) => ({ ...p, tur: v, kategori: v === "alacak" ? "tahsilat" : "hizmet_ucreti" }))}>
                            <SelectTrigger data-testid="txn-tur" className="rounded-none bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                            <SelectContent className="rounded-none bg-zinc-900 border-zinc-800">
                              <SelectItem value="borc">Borç (Tahakkuk / Ücret)</SelectItem>
                              <SelectItem value="alacak">Alacak (Tahsilat)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-zinc-400">Tarih</Label>
                          <Input type="date" data-testid="txn-tarih" value={txnForm.tarih} onChange={(e) => setTxnForm((p) => ({ ...p, tarih: e.target.value }))} className="rounded-none bg-zinc-900 border-zinc-800" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-zinc-400">Açıklama</Label>
                        <Input data-testid="txn-aciklama" value={txnForm.aciklama} onChange={(e) => setTxnForm((p) => ({ ...p, aciklama: e.target.value }))} className="rounded-none bg-zinc-900 border-zinc-800" placeholder="Örn: Ocak ayı tahsilatı" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-zinc-400">Tutar (₺)</Label>
                        <Input type="number" data-testid="txn-tutar" value={txnForm.tutar} onChange={(e) => setTxnForm((p) => ({ ...p, tutar: e.target.value }))} className="rounded-none bg-zinc-900 border-zinc-800 font-mono" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button data-testid="txn-save-btn" onClick={addTxn} className="rounded-none bg-white text-black hover:bg-zinc-200 font-semibold w-full">Kaydet</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="border border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest">Tarih</TableHead>
                      <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest">Açıklama</TableHead>
                      <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest text-right">Borç</TableHead>
                      <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest text-right">Alacak</TableHead>
                      <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest text-right">Bakiye</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txns.length === 0 && (
                      <TableRow className="border-zinc-800 hover:bg-transparent"><TableCell colSpan={6} className="text-center py-12 text-zinc-500 text-sm">Henüz cari hareket yok.</TableCell></TableRow>
                    )}
                    {txns.map((t) => (
                      <TableRow key={t.id} data-testid={`txn-row-${t.id}`} className="border-zinc-900 hover:bg-zinc-900 group">
                        <TableCell className="font-mono text-sm text-zinc-400">{fmtDate(t.tarih)}</TableCell>
                        <TableCell className="text-sm">{t.aciklama}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-red-400">{t.tur === "borc" ? fmtTRY(t.tutar) : "-"}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-green-400">{t.tur === "alacak" ? fmtTRY(t.tutar) : "-"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtTRY(t.yuruyen_bakiye)}</TableCell>
                        <TableCell className="text-right">
                          <button data-testid={`del-txn-${t.id}`} onClick={() => delTxn(t.id)} className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Belgeler */}
            <TabsContent value="belgeler" className="p-5 mt-0">
              <div className="flex items-end gap-3 mb-5 flex-wrap">
                <div className="space-y-1.5">
                  <Label className="text-xs text-zinc-400">Belge Tipi</Label>
                  <Select value={belgeTipi} onValueChange={setBelgeTipi}>
                    <SelectTrigger data-testid="belge-tipi-select" className="rounded-none bg-zinc-900 border-zinc-800 w-56"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-zinc-900 border-zinc-800 max-h-72">
                      {BELGE_TIPLERI.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <input ref={fileRef} type="file" className="hidden" data-testid="file-input" onChange={uploadDoc} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" />
                <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()} className="rounded-none bg-white text-black hover:bg-zinc-200 font-semibold"><Upload className="w-4 h-4 mr-1.5" /> Belge Yükle</Button>
              </div>

              <div className="border border-zinc-800" data-testid="documents-list">
                {docs.length === 0 && <div className="p-10 text-center text-zinc-500 text-sm">Yüklenmiş belge yok.</div>}
                {docs.map((d) => (
                  <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between px-4 py-3 border-b border-zinc-900 hover:bg-zinc-900 group">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-zinc-500 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm truncate">{d.original_filename}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="rounded-none text-[10px] border-zinc-700 text-zinc-400">{d.belge_tipi}</Badge>
                          <span className="text-xs text-zinc-600">{(d.size / 1024).toFixed(0)} KB</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`view-doc-${d.id}`} className="p-2 text-zinc-500 hover:text-white"><Download className="w-4 h-4" /></a>
                      <button data-testid={`del-doc-${d.id}`} onClick={() => delDoc(d.id)} className="p-2 text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Kart Bilgileri */}
            <TabsContent value="bilgiler" className="p-5 mt-0 space-y-5">
              {FORM_SECTIONS.map((sec) => {
                if (sec.risk) {
                  const answered = RISK_SORULARI.filter((q) => k[q.key]);
                  if (answered.length === 0) return null;
                  return (
                    <div key={sec.id}>
                      <div className="text-sm font-semibold mb-2">{sec.title}</div>
                      {answered.map((q) => (
                        <div key={q.key} className="py-2 border-b border-zinc-900 flex items-center justify-between gap-3">
                          <span className="text-xs text-zinc-500">{q.label}</span>
                          <Badge className={`rounded-none text-[10px] ${k[q.key] === "Evet" ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-green-500/15 text-green-400 border-green-500/30"}`}>{k[q.key]}</Badge>
                        </div>
                      ))}
                    </div>
                  );
                }
                const items = [];
                sec.fields?.forEach((f) => { const el = renderVal(f, k[f.key]); if (el) items.push(el); });
                sec.checklist?.forEach((c) => { if (k[c.key]) items.push(<div key={c.key} className="py-2 border-b border-zinc-900 grid grid-cols-2 gap-2"><span className="text-xs text-zinc-500">{c.label}</span><span className="text-sm text-green-400">✓</span></div>); });
                if (items.length === 0) return null;
                return (
                  <div key={sec.id}>
                    <div className="text-sm font-semibold mb-2">{sec.title}</div>
                    {items}
                  </div>
                );
              })}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
